/**
 * DrilldownMap - Mapbox map showing well locations
 * @module wellRegistrationTotals/components/organisms/DrilldownPanel/DrilldownMap
 */

import React, { useRef, useEffect, useMemo } from "react";
import mapboxgl from "!mapbox-gl"; // eslint-disable-line import/no-webpack-loader-syntax
import { Paper, Typography } from "@material-ui/core";
import { MapContainer, MapScrollContainer } from "../../../styled";
import { STARTING_LOCATION } from "../../../../../../../constants";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

/**
 * Map showing well locations for drilldown
 * @param {Object} props
 * @param {Array} props.wells - Array of well objects with lat/lng
 * @param {Object} [props.initialView] - Initial map view (center, zoom)
 * @param {Function} [props.onViewChange] - Callback when map view changes
 */
const DrilldownMap = React.memo(({ wells, initialView, onViewChange }) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);

  const geojsonData = useMemo(() => {
    const features = wells
      .filter((w) => w.latitude_dd && w.longitude_dd)
      .map((well) => ({
        type: "Feature",
        properties: {
          well_ndx: well.well_ndx,
          label: well.cuwcd_well_number || "",
        },
        geometry: {
          type: "Point",
          coordinates: [well.longitude_dd, well.latitude_dd],
        },
      }));

    return {
      type: "FeatureCollection",
      features,
    };
  }, [wells]);

  useEffect(() => {
    if (!mapContainerRef.current) return;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: "mapbox://styles/mapbox/streets-v11",
      center: initialView?.center || STARTING_LOCATION,
      zoom: initialView?.zoom || 9,
      attributionControl: false,
      preserveDrawingBuffer: true,
    });

    mapRef.current = map;

    map.on("load", () => {
      map.addSource("wells", {
        type: "geojson",
        data: geojsonData,
      });

      map.addLayer({
        id: "wells-circles",
        type: "circle",
        source: "wells",
        paint: {
          "circle-radius": 8,
          "circle-color": "#1976d2",
          "circle-stroke-width": 2,
          "circle-stroke-color": "#ffffff",
        },
      });

      map.addLayer({
        id: "wells-labels",
        type: "symbol",
        source: "wells",
        layout: {
          "text-field": ["get", "label"],
          "text-size": 12,
          "text-offset": [0, -1.5],
          "text-anchor": "bottom",
          "text-allow-overlap": false,
        },
        paint: {
          "text-color": "#1976d2",
          "text-halo-color": "#ffffff",
          "text-halo-width": 2,
        },
      });

      if (initialView) {
        map.jumpTo({ center: initialView.center, zoom: initialView.zoom });
      } else {
        // Fit bounds to markers
        if (geojsonData.features.length > 0) {
          const bounds = new mapboxgl.LngLatBounds();
          geojsonData.features.forEach((feature) => {
            bounds.extend(feature.geometry.coordinates);
          });
          map.fitBounds(bounds, { padding: 50, maxZoom: 14, duration: 0 });
        }

        // Report view changes
        if (onViewChange) {
          const reportView = () => {
            const center = map.getCenter();
            onViewChange({
              center: [center.lng, center.lat],
              zoom: map.getZoom(),
            });
          };
          reportView();
          map.on("moveend", reportView);
        }
      }
    });

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [initialView, geojsonData, onViewChange]);

  const wellsWithCoords = wells.filter(
    (w) => w.latitude_dd && w.longitude_dd
  ).length;

  if (wellsWithCoords === 0) {
    return (
      <Paper
        variant="outlined"
        style={{
          height: 100,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 16,
        }}
      >
        <Typography variant="body2" color="textSecondary">
          No wells with coordinates to display on map
        </Typography>
      </Paper>
    );
  }

  return (
    <MapScrollContainer>
      <MapContainer ref={mapContainerRef} />
    </MapScrollContainer>
  );
});

DrilldownMap.displayName = "DrilldownMap";

export default DrilldownMap;
